import { useEffect, useRef, useState } from "react";
import { axiosInstance } from "@/contexts/axiosApiInstance";
import type { UnitDetails } from "./types";

// Catalog rows are static within a session — same TTL philosophy as
// `useCommonUnits`. The post-deploy version gate is what refreshes.
const TIMEOUT_MS = 8000;

export interface UnitLookupState {
  loading: boolean;
  details: UnitDetails | null;
  /** `true` when the server returned 404 — distinct from a transient error. */
  notFound: boolean;
  error: string | null;
}

const IDLE: UnitLookupState = {
  loading: false,
  details: null,
  notFound: false,
  error: null,
};

/**
 * Resolve a single unit URI against the full catalog via `/api/units/lookup`.
 *
 * Originally the picker tried to find the current assignment's details by
 * grep'ing the 20-entry curated commons list, which falsely flagged any
 * non-curated URI (~99 % of the catalog) as "no longer in catalog" on
 * picker reopen. This hook always targets the full catalog directly.
 *
 * Per-instance Map cache keeps repeated lookups (e.g. current assignment +
 * heuristic match resolving to the same URI) to a single network round-trip.
 * `null`/`undefined` URI → idle state, no fetch.
 */
export function useUnitLookup(uri: string | null | undefined): UnitLookupState {
  const [state, setState] = useState<UnitLookupState>(IDLE);
  const cacheRef = useRef<Map<string, UnitLookupState>>(new Map());

  useEffect(() => {
    if (!uri) {
      setState(IDLE);
      return;
    }
    const cached = cacheRef.current.get(uri);
    if (cached) {
      setState(cached);
      return;
    }

    const controller = new AbortController();
    let cancelled = false;
    setState({ loading: true, details: null, notFound: false, error: null });

    axiosInstance
      .get<UnitDetails>("/api/units/lookup", {
        params: { uri },
        signal: controller.signal,
        timeout: TIMEOUT_MS,
      })
      .then((res) => {
        if (cancelled) return;
        const next: UnitLookupState = {
          loading: false,
          details: res.data,
          notFound: false,
          error: null,
        };
        cacheRef.current.set(uri, next);
        setState(next);
      })
      .catch((err) => {
        if (cancelled || controller.signal.aborted) return;
        // 404 is the "URI genuinely not in catalog" signal — that's what the
        // badge's missing-state UI is for. Cache it so we don't re-hit the
        // server on every picker reopen for a stale URI.
        if (err?.response?.status === 404) {
          const next: UnitLookupState = {
            loading: false,
            details: null,
            notFound: true,
            error: null,
          };
          cacheRef.current.set(uri, next);
          setState(next);
          return;
        }
        const isTimeout =
          err?.code === "ECONNABORTED" || err?.message?.includes("timeout");
        setState({
          loading: false,
          details: null,
          notFound: false,
          error: isTimeout ? "Lookup timed out" : "Lookup failed",
        });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [uri]);

  return state;
}
